import { ErrorView, PageLayout } from "@gearbox-protocol/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { SkeletonBytecodeDetails } from "@/components/bytecode/skeleton-bytecode-details";
import { BytecodeRepositoryDetailsView } from "@/components/bytecode/view-bytecode-details";
import { BytecodeInfo, BytecodeShort } from "@/core/bytecode";
import { ApiError } from "@/core/error";
import { Instance } from "@/core/instance";
import { fetchGearboxApis } from "@/utils/api-fetch";

export function BytecodeTypePage() {
  const { contractType, version } = useParams<{
    contractType: string;
    version: string;
  }>();

  const { data, error, isLoading } = useQuery<
    [{ data: Instance[] }, BytecodeInfo, BytecodeShort[]],
    Error
  >({
    queryKey: ["bytecode-type", contractType, version],
    queryFn: async () => {
      if (!contractType || !version) {
        throw new ApiError("Contract type and version are required", 400);
      }
      const decodedContractType = decodeURIComponent(contractType);
      return fetchGearboxApis<
        [{ data: Instance[] }, BytecodeInfo, BytecodeShort[]]
      >(
        ["/chain/list", `/bcr/type/${decodedContractType}/${version}`, "/bcr"],
        {
          errorMessage: "Failed to fetch bytecode",
        },
      );
    },
    enabled: !!contractType && !!version,
  });

  if (isLoading) {
    return (
      <PageLayout title="Bytecode Details">
        <SkeletonBytecodeDetails />
      </PageLayout>
    );
  }

  if (error || !data) {
    const status = error instanceof ApiError ? error.status : 500;
    const message =
      error instanceof Error ? error.message : "Unknown error occurred";

    return (
      <ErrorView
        status={status}
        message={message}
        backButton={{
          text: "Back to bytecodes",
          href: "/",
        }}
      />
    );
  }

  const [chains, bytecode, listBytecodes] = data;

  return (
    <BytecodeRepositoryDetailsView
      bytecode={bytecode}
      listBytecodes={listBytecodes}
      instances={chains.data}
    />
  );
}
